import * as XLSX from 'xlsx';

const COLUMNS = [
  'Min Amount *', 'Max Amount *', 'Tax % *', 'Deduction Amount *'
];

export const downloadTaxTableTemplate = () => {
  const wb = XLSX.utils.book_new();
  const sampleData = [{
    'Min Amount *': 0,
    'Max Amount *': 100000,
    'Tax % *': 6,
    'Deduction Amount *': 0,
  }];
  
  const ws = XLSX.utils.json_to_sheet(sampleData, { header: COLUMNS });
  ws['!cols'] = COLUMNS.map(() => ({ wch: 20 }));
  
  XLSX.utils.book_append_sheet(wb, ws, 'TaxRules');
  XLSX.writeFile(wb, 'Tax_Table_Import_Template.xlsx');
};

export const readTaxTableWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) return [];

  const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '' });
  
  return rawRows.map((row, idx) => ({
    _row: idx + 2,
    minAmount: String(row['Min Amount *'] ?? '').trim(),
    maxAmount: String(row['Max Amount *'] ?? '').trim(),
    taxPercentage: String(row['Tax % *'] ?? '').trim(),
    deducAmount: String(row['Deduction Amount *'] ?? '').trim(),
  }));
};

export const validateTaxTableBatch = (rawRows, existingRules, mode, activeTableNo) => {
  // 1. Process individual rows for basic validation
  const processed = rawRows.map((row) => {
    const errors = [];
    let minAmount = 0, maxAmount = 0, taxPercentage = 0, deducAmount = 0;

    if (row.minAmount === '') errors.push('Min Amount is required');
    else {
      minAmount = Number(row.minAmount.replace(/,/g, ''));
      if (isNaN(minAmount)) errors.push('Min Amount must be a valid number');
    }

    if (row.maxAmount === '') errors.push('Max Amount is required');
    else {
      maxAmount = Number(row.maxAmount.replace(/,/g, ''));
      if (isNaN(maxAmount)) errors.push('Max Amount must be a valid number');
    }

    if (!isNaN(minAmount) && !isNaN(maxAmount) && maxAmount < minAmount) {
      errors.push('Max Amount must be greater than Min Amount');
    }

    if (row.taxPercentage === '') errors.push('Tax percentage is required');
    else {
      taxPercentage = Number(row.taxPercentage);
      if (isNaN(taxPercentage) || taxPercentage < 0 || taxPercentage > 100) {
        errors.push('Tax % must be between 0 and 100');
      }
    }

    if (row.deducAmount === '') errors.push('Deduction Amount is required');
    else {
      deducAmount = Number(row.deducAmount.replace(/,/g, ''));
      if (isNaN(deducAmount)) errors.push('Deduction Amount must be a valid number');
    }

    return {
      _row: row._row,
      values: {
        minAmount,
        maxAmount,
        taxPercentage,
        deducAmount,
        incomeRange: `${minAmount.toLocaleString()} - ${maxAmount.toLocaleString()}`,
      },
      errors,
      _rawMin: minAmount,
      _rawMax: maxAmount,
      _isValid: errors.length === 0
    };
  });

  // 2. Overlap & Duplicate Validation
  const allRanges = [];

  // If appending to existing, inject the active table's existing rules into the overlap check
  if (mode === 'existing' && activeTableNo && Array.isArray(existingRules)) {
    existingRules
      .filter(r => String(r.HTT_TABLE_NO) === String(activeTableNo))
      .forEach(r => {
        const min = Number(r.HTT_MIN_AMOUNT);
        const max = Number(r.HTT_MAX_AMOUNT);
        if (!isNaN(min) && !isNaN(max)) {
          allRanges.push({ min, max, isExisting: true, ref: null });
        }
      });
  }

  // Inject the valid incoming rows into the overlap check
  processed.forEach(p => {
    if (p._isValid) {
      allRanges.push({ min: p._rawMin, max: p._rawMax, isExisting: false, ref: p });
    }
  });

  // Sort strictly by the minimum amount
  allRanges.sort((a, b) => a.min - b.min);

  // Compare each range to the next one
  for (let i = 0; i < allRanges.length - 1; i++) {
    const current = allRanges[i];
    const next = allRanges[i + 1];

    // If current max is greater than or equal to next min, it overlaps 
    // (e.g., 20000 >= 20000 is an overlap. Next must start at 20001)
    if (current.max >= next.min) {
      const overlapMsg = `Range overlaps with ${next.min.toLocaleString()} - ${next.max.toLocaleString()}`;
      const overlapMsgNext = `Range overlaps with ${current.min.toLocaleString()} - ${current.max.toLocaleString()}`;

      if (!current.isExisting && !current.ref.errors.includes(overlapMsg)) {
        current.ref.errors.push(overlapMsg);
      }
      if (!next.isExisting && !next.ref.errors.includes(overlapMsgNext)) {
        next.ref.errors.push(overlapMsgNext);
      }
    }
  }

  // 3. Clean up the internal properties and return the standard array
  return processed.map(p => ({
    _row: p._row,
    values: p.values,
    errors: p.errors
  }));
};